/**
 * Shared React Query key factory, fetch funkcije i hook-ovi za action-plan/search API.
 * Koriste Admin workspace, ActionPlanListPage, Home i ActionPlan detail.
 * Deduplikacija: više komponenti koje mount-uju istovremeno dele jedan mrežni zahtev.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import ActionPlanService from '../../../shared/services/ActionPlanService';
import { obligationKeys } from './obligationQueries';

/** Query key factory za action plan keš. */
export const actionPlanKeys = {
  all: ['actionPlan'],
  search: () => [...actionPlanKeys.all, 'search'],
  /** Jedan ključ za POST /action-plan/search-v2 (profil, početna) — deduplikacija u letu i u kešu. */
  searchV2: () => [...actionPlanKeys.all, 'searchV2'],
  detail: (id) => [...actionPlanKeys.all, 'detail', id],
  statusHistory: (id) => [...actionPlanKeys.all, 'statusHistory', id],
};

const STALE_TIME_MS = 60 * 1000;

/**
 * Fetch za POST /action-plan/search (legacy lista).
 * @returns {Promise<{ action_plans: object[] }>} Normalizovan odgovor sa nizom akcionih planova.
 */
export async function fetchActionPlanSearch() {
  const data = await ActionPlanService.search();
  return data ?? { action_plans: [] };
}

/**
 * Normalizovan odgovor search-v2 (draft / implementation / completed).
 * Deli se između useHomeCoreQuery i profilnog taba obaveza.
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - Signal za otkazivanje zahteva.
 * @returns {Promise<{ draft: object|null, implementation: object|null, completed: object[] }>}
 */
export async function fetchActionPlanSearchV2({ signal } = {}) {
  const data = await ActionPlanService.searchV2(signal);
  return {
    draft: data?.draft ?? null,
    implementation: data?.implementation ?? null,
    completed: Array.isArray(data?.completed) ? data.completed : [],
  };
}

/**
 * React Query hook za action-plan/search — deljeni keš između svih potrošača.
 *
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useActionPlanSearchQuery(options = {}) {
  return useQuery({
    queryKey: actionPlanKeys.search(),
    queryFn: fetchActionPlanSearch,
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * React Query hook za action-plan/search-v2 (draft / implementation / completed slotovi).
 *
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useActionPlanSearchV2Query(options = {}) {
  return useQuery({
    queryKey: actionPlanKeys.searchV2(),
    queryFn: fetchActionPlanSearchV2,
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * Fetch istorije statusa akcionog plana, sortirane po `status_valid_from`.
 *
 * @param {string|number} actionPlanId - ID akcionog plana.
 * @returns {Promise<object[]>} Sortiran niz status zapisa.
 */
export async function fetchActionPlanStatusHistory(actionPlanId) {
  const response = await ActionPlanService.getStatusHistory(actionPlanId);
  let history = response?.history || [];

  history = [...history].sort((a, b) => {
    const dateA = a.status_valid_from ? new Date(a.status_valid_from).getTime() : 0;
    const dateB = b.status_valid_from ? new Date(b.status_valid_from).getTime() : 0;
    return dateA - dateB;
  });

  return history;
}

/**
 * React Query hook za istoriju statusa jednog akcionog plana.
 *
 * @param {string|number} actionPlanId - ID akcionog plana.
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult<object[]>}
 */
export function useActionPlanStatusHistoryQuery(actionPlanId, options = {}) {
  return useQuery({
    queryKey: actionPlanKeys.statusHistory(actionPlanId),
    queryFn: () => fetchActionPlanStatusHistory(actionPlanId),
    enabled: Boolean(actionPlanId),
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * Vraća QueryClient instancu (helper za cache operacije u action plan modulu).
 *
 * @returns {import('@tanstack/react-query').QueryClient}
 */
export function useActionPlanSearchQueryClient() {
  return useQueryClient();
}

/**
 * Optimistički ažurira status jednog AP-a u kešu action-plan/search.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - React Query klijent.
 * @param {string|number} planId - ID akcionog plana.
 * @param {string} status - Novi status.
 * @returns {void}
 */
export function patchActionPlanStatusInSearchCache(queryClient, planId, status) {
  queryClient.setQueryData(actionPlanKeys.search(), (old) => {
    if (!old?.action_plans) return old;
    return {
      ...old,
      action_plans: old.action_plans.map((ap) =>
        String(ap.id) === String(planId) ? { ...ap, status } : ap,
      ),
    };
  });
}

/**
 * Optimistički ažurira search-v2 keš (početna: draft / implementation / completed).
 * Premješta AP u odgovarajući slot prema novom statusu.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - React Query klijent.
 * @param {string|number} planId - ID akcionog plana.
 * @param {string} newStatus - Novi status.
 * @returns {void}
 */
export function patchActionPlanStatusInSearchV2Cache(queryClient, planId, newStatus) {
  queryClient.setQueryData(actionPlanKeys.searchV2(), (old) => {
    if (!old) return old;

    const id = String(planId);
    const matches = (ap) => ap && String(ap.id) === id;
    const status = String(newStatus || '').toUpperCase();

    let draft = old.draft;
    let implementation = old.implementation;
    let completed = [...(old.completed || [])];

    const findAp = () => {
      if (matches(draft)) return { ...draft, status: newStatus };
      if (matches(implementation)) return { ...implementation, status: newStatus };
      const fromCompleted = completed.find(matches);
      if (fromCompleted) return { ...fromCompleted, status: newStatus };
      const list = queryClient.getQueryData(actionPlanKeys.search())?.action_plans;
      const fromList = list?.find((p) => String(p.id) === id);
      return fromList ? { ...fromList, status: newStatus } : null;
    };

    const ap = findAp();
    if (!ap) return old;

    if (status === 'IMPLEMENTATION' || status === 'REVISION') {
      if (matches(draft)) draft = null;
      completed = completed.filter((c) => !matches(c));
      implementation = ap;
    } else if (status === 'DRAFT') {
      if (matches(implementation)) implementation = null;
      completed = completed.filter((c) => !matches(c));
      draft = ap;
    } else if (status === 'COMPLETED') {
      if (matches(draft)) draft = null;
      if (matches(implementation)) implementation = null;
      completed = completed.some(matches)
        ? completed.map((c) => (matches(c) ? ap : c))
        : [...completed, ap];
    } else {
      if (matches(draft)) draft = ap;
      if (matches(implementation)) implementation = ap;
      completed = completed.map((c) => (matches(c) ? ap : c));
    }

    return { draft, implementation, completed };
  });
}

/**
 * Invalidira i refetch-uje sve action-plan query-je (search + search-v2).
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - React Query klijent.
 * @returns {Promise<void>}
 */
export async function refetchActionPlanSearch(queryClient) {
  await queryClient.invalidateQueries({
    queryKey: actionPlanKeys.all,
    refetchType: 'all',
  });
}

/**
 * Posle promene statusa: patch keševa + refetch (admin, početna, sidebar) i invalidacija obaveza.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - React Query klijent.
 * @param {string|number} planId - ID akcionog plana.
 * @param {string} newStatus - Novi status.
 * @returns {Promise<void>}
 */
export async function syncActionPlanSearchAfterStatusChange(queryClient, planId, newStatus) {
  patchActionPlanStatusInSearchCache(queryClient, planId, newStatus);
  patchActionPlanStatusInSearchV2Cache(queryClient, planId, newStatus);
  await Promise.all([
    refetchActionPlanSearch(queryClient),
    queryClient.invalidateQueries({
      queryKey: obligationKeys.all,
      refetchType: 'all',
    }),
  ]);
}
